<template>
  <section class="overflow-hidden rounded-xl border border-blue-300 bg-white shadow-sm" aria-labelledby="po-tracker-updates">
    <header class="flex flex-wrap items-center gap-x-3 gap-y-2 bg-blue-50 px-4 py-2.5">
      <span class="h-2 w-2 flex-none rounded-full bg-blue-600" aria-hidden="true"></span>
      <h2 id="po-tracker-updates" class="m-0 flex-1 text-sm font-semibold text-gray-900">{{ heading }}</h2>
      <button type="button" class="btn-secondary btn-sm" :disabled="busy" @click="markSeen">{{ busy ? 'Marking as seen…' : 'Mark all as seen' }}</button>
    </header>
    <ul class="m-0 list-none p-0">
      <li v-for="event in shown" :key="event.id" class="grid grid-cols-1 items-baseline gap-x-3 border-t border-gray-100 px-4 py-2 text-sm sm:grid-cols-[90px_minmax(0,1fr)_auto]">
        <button type="button" class="w-fit font-semibold text-blue-700 hover:underline" @click="emit('open-po', event.poNumber ?? '')">{{ event.poNumber }}</button>
        <span class="min-w-0 text-gray-900">
          <span class="font-semibold">{{ event.actor.name }}</span>
          {{ describeTrackerEvent(event) }}
        </span>
        <span class="whitespace-nowrap text-xs text-gray-500">{{ relativeWhen(event.createdAt, now) }}</span>
      </li>
    </ul>
    <button v-if="events.length > SHOWN" type="button" class="px-4 pb-2.5 pt-2 text-sm font-semibold text-blue-700 hover:underline" @click="emit('show-all')">
      Show all {{ events.length }} in the table
    </button>
  </section>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';

  import { usePoTrackerStore } from '@/store/poTrackerStore';
  import type { TrackerUpdates } from '@/types/poTracker';
  import { describeTrackerEvent, relativeWhen, updatesHeading } from '@/utils/poTrackerEvents';

  const SHOWN = 5;

  const props = defineProps<{ updates: TrackerUpdates; vendorName: string }>();
  const emit = defineEmits<{ 'open-po': [poNumber: string]; 'show-all': [] }>();

  const store = usePoTrackerStore();
  const busy = ref(false);
  const now = new Date();

  const events = computed(() => props.updates.events.filter((event) => event.poNumber !== null).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
  const shown = computed(() => events.value.slice(0, SHOWN));
  const heading = computed(() => updatesHeading(events.value.length, props.vendorName, props.updates.seenAt, now));

  async function markSeen(): Promise<void> {
    busy.value = true;
    try {
      await store.markSeen();
    } catch {
      // The global interceptor already toasted.
    } finally {
      busy.value = false;
    }
  }
</script>
